import { Analytics, getAnalytics, isSupported } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, onAuthStateChanged, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updateProfile, User } from "firebase/auth";
import { createLogger } from "../utils/logger";
import { ANALYTICS_ERROR_CODES, analyticsService } from "./AnalyticsService";
import { userPropertiesService } from "./UserPropertiesService";

const logger = createLogger("FirebaseService");

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Validate required Firebase configuration
const requiredEnvVars = [
  "REACT_APP_FIREBASE_API_KEY",
  "REACT_APP_FIREBASE_AUTH_DOMAIN",
  "REACT_APP_FIREBASE_PROJECT_ID",
  "REACT_APP_FIREBASE_STORAGE_BUCKET",
  "REACT_APP_FIREBASE_MESSAGING_SENDER_ID",
  "REACT_APP_FIREBASE_APP_ID",
  "REACT_APP_FIREBASE_MEASUREMENT_ID",
];

for (const envVar of requiredEnvVars) {
  if (!process.env[envVar]) {
    throw new Error(`Missing required environment variable: ${envVar}`);
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Initialize Analytics (only in production and if supported)
let analytics: Analytics | null = null;
const initializeAnalytics = async () => {
  try {
    // Only initialize analytics in production or when explicitly enabled
    const isProduction = process.env.NODE_ENV === "production";
    const analyticsEnabled = process.env.REACT_APP_ANALYTICS_ENABLED === "true";

    if ((isProduction || analyticsEnabled) && process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
      const supported = await isSupported();
      if (supported) {
        analytics = getAnalytics(app);
        logger.info("Firebase Analytics initialized successfully");
      } else {
        logger.warn("Firebase Analytics not supported in this environment");
      }
    } else {
      logger.info("Firebase Analytics disabled for development environment");
    }
  } catch (error) {
    logger.error("Failed to initialize Firebase Analytics:", error);
  }
};

// Initialize analytics
initializeAnalytics();

// Connect to Firebase Auth emulator if requested
if (process.env.REACT_APP_USE_FIREBASE_AUTH_EMULATOR === "true") {
  if (process.env.REACT_APP_AUTH_EMULATOR_HOST) {
    connectAuthEmulator(auth, `http://${process.env.REACT_APP_AUTH_EMULATOR_HOST}`, { disableWarnings: true });
  }
}

// Firebase Functions base URL - must be set in environment
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;
if (!FUNCTIONS_BASE_URL) {
  throw new Error("REACT_APP_FUNCTIONS_BASE_URL environment variable is required");
}

// Authentication functions
export const registerWithEmail = async (email: string, password: string, displayName: string): Promise<User> => {
  try {
    // Use custom registration endpoint that handles approval status
    const response = await fetch(FUNCTIONS_BASE_URL + "/registerUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
        displayName,
      }),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        // If response is not JSON, use status text
        throw new Error(`Registration failed: ${response.statusText}`);
      }

      // Handle registration blocking
      if (response.status === 403 && errorData.code === "PENDING_APPROVAL") {
        logger.info("Registration blocked for email:", email);
        throw new Error("REGISTRATION_BLOCKED");
      }

      // Handle specific error cases
      if (response.status === 400) {
        throw new Error(errorData.error || "Invalid registration data");
      } else if (response.status === 500) {
        throw new Error("Registration service is temporarily unavailable. Please try again later.");
      }

      throw new Error(errorData.message || errorData.error || "Registration failed");
    }

    const result = await response.json();
    logger.info("Custom registration successful:", result);

    // Now sign in with the created user
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update profile with display name if not already set
    if (!user.displayName && displayName) {
      await updateProfile(user, { displayName });
    }

    // Send verification email
    await sendVerificationEmail(user);

    // Track successful registration
    analyticsService.logAuthEvent("register", "email");
    analyticsService.setUser(user);
    userPropertiesService.setUserProperties(user);

    return user;
  } catch (error: any) {
    logger.error("Error registering with email:", error);

    // Check if this is a registration restriction error
    if (error.message === "REGISTRATION_BLOCKED") {
      // This is a blocked registration - don't track as a regular error
      logger.info("Registration blocked for email:", email);
      throw new Error("REGISTRATION_BLOCKED");
    }

    // Track registration failure for other errors
    analyticsService.logAuthEvent("register_failed", "email", ANALYTICS_ERROR_CODES.AUTH_REGISTRATION_FAILED);
    analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_REGISTRATION_FAILED, error instanceof Error ? error.message : "Unknown registration error", {
      component: "firebase.ts",
      userAction: "register_with_email",
      severity: "high",
    });

    throw error;
  }
};

export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if email is verified
    if (!user.emailVerified) {
      logger.warn("Login attempt with unverified email:", email);
      // Don't sign out here - let App.tsx handle the email verification flow
      throw new Error("EMAIL_NOT_VERIFIED");
    }

    // Call the login function to update user record
    try {
      await apiCall("/onUserLogin", {
        method: "POST",
        body: JSON.stringify({}),
      });
      logger.debug("Login recorded in user record");
    } catch (loginError) {
      logger.error("Failed to record login in user record:", loginError);
      // Track API error but don't fail the login
      analyticsService.logError(ANALYTICS_ERROR_CODES.API_CALL_FAILED, "Failed to record login in user record", {
        component: "firebase.ts",
        userAction: "onUserLogin",
        severity: "medium",
      });
    }

    // Track successful login
    analyticsService.logAuthEvent("login", "email");
    analyticsService.setUser(user);
    userPropertiesService.setUserProperties(user);

    return user;
  } catch (error) {
    logger.error("Error signing in with email:", error);

    // Track login failure
    analyticsService.logAuthEvent("login_failed", "email", ANALYTICS_ERROR_CODES.AUTH_LOGIN_FAILED);
    analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_LOGIN_FAILED, error instanceof Error ? error.message : "Unknown login error", {
      component: "firebase.ts",
      userAction: "sign_in_with_email",
      severity: "high",
    });

    throw error;
  }
};

export const signOutUser = async (): Promise<void> => {
  try {
    // Track logout before signing out
    analyticsService.logAuthEvent("logout", "unknown");

    await signOut(auth);

    // Clear user from analytics
    analyticsService.setUser(null);
    userPropertiesService.clearUserProperties();
  } catch (error) {
    logger.error("Error signing out:", error);

    // Track logout failure
    analyticsService.logAuthEvent("logout", "unknown", ANALYTICS_ERROR_CODES.AUTH_LOGOUT_FAILED);
    analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_LOGOUT_FAILED, error instanceof Error ? error.message : "Unknown logout error", {
      component: "firebase.ts",
      userAction: "sign_out",
      severity: "medium",
    });

    throw error;
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Get auth token for API calls
export const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;

  if (user) {
    try {
      const token = await user.getIdToken();
      return token;
    } catch (error) {
      logger.error("Firebase getAuthToken: Error getting token:", error);
      return null;
    }
  }
  return null;
};

// API call helper with authentication
export const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const startTime = Date.now();
  const requestMethod = options.method || "GET";

  try {
    const token = await getAuthToken();

    // Don't include Authorization header on OPTIONS requests to avoid CORS issues
    const isOptionsRequest = requestMethod === "OPTIONS";

    const headers = {
      "Content-Type": "application/json",
      ...(token && !isOptionsRequest && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const fullUrl = FUNCTIONS_BASE_URL + endpoint;

    const response = await fetch(fullUrl, {
      ...options,
      method: requestMethod,
      headers,
    });

    const responseTime = Date.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Firebase apiCall: Error response body:", errorText);

      // Track API errors with specific error codes
      let errorCode: string = ANALYTICS_ERROR_CODES.API_CALL_FAILED;
      if (response.status === 401) errorCode = ANALYTICS_ERROR_CODES.API_UNAUTHORIZED;
      else if (response.status === 403) errorCode = ANALYTICS_ERROR_CODES.API_FORBIDDEN;
      else if (response.status === 404) errorCode = ANALYTICS_ERROR_CODES.API_NOT_FOUND;
      else if (response.status >= 500) errorCode = ANALYTICS_ERROR_CODES.API_SERVER_ERROR;

      analyticsService.logError(errorCode, `API call failed: ${response.statusText}`, {
        component: "firebase.ts",
        userAction: `api_call_${requestMethod.toLowerCase()}`,
        severity: response.status >= 500 ? "high" : "medium",
      });

      throw new Error(`API call failed: ${response.statusText}`);
    }

    const responseData = await response.json();

    // Track successful API calls for performance monitoring
    if (responseTime > 5000) {
      // Log slow API calls
      analyticsService.logError(ANALYTICS_ERROR_CODES.API_TIMEOUT, `Slow API call: ${responseTime}ms`, {
        component: "firebase.ts",
        userAction: `api_call_${requestMethod.toLowerCase()}`,
        severity: "low",
      });
    }

    return responseData;
  } catch (error) {
    const responseTime = Date.now() - startTime;

    // Track network errors
    if (error instanceof TypeError && error.message.includes("fetch")) {
      analyticsService.logError(ANALYTICS_ERROR_CODES.API_NETWORK_ERROR, "Network error during API call", {
        component: "firebase.ts",
        userAction: `api_call_${requestMethod.toLowerCase()}`,
        severity: "high",
      });
    }

    throw error;
  }
};

// API call helper without Firebase authentication (for Google OAuth users)
export const apiCallWithoutAuth = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  const fullUrl = FUNCTIONS_BASE_URL + endpoint;

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`API call failed: ${response.statusText}`);
  }

  return response.json();
};

// Email verification functions
export const sendVerificationEmail = async (user: User): Promise<void> => {
  try {
    await sendEmailVerification(user);
    logger.info("Verification email sent successfully");
    analyticsService.logAuthEvent("verification_email_sent", "email");
  } catch (error) {
    logger.error("Error sending verification email:", error);
    analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_VERIFICATION_FAILED, error instanceof Error ? error.message : "Unknown verification error", {
      component: "firebase.ts",
      userAction: "send_verification_email",
      severity: "medium",
    });
    throw error;
  }
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
    logger.info("Password reset email sent successfully");
    analyticsService.logAuthEvent("password_reset_sent", "email");
  } catch (error) {
    logger.error("Error sending password reset email:", error);
    analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_PASSWORD_RESET_FAILED, error instanceof Error ? error.message : "Unknown password reset error", {
      component: "firebase.ts",
      userAction: "send_password_reset",
      severity: "medium",
    });
    throw error;
  }
};

export { analytics, auth, FUNCTIONS_BASE_URL, signOut };
